/**
 * YouTube URL helpers shared by the Learn surface (community grid + tutorial
 * lessons). Lets a `videoUrl` be a plain YouTube link that renders as a native
 * embed (`YouTubePlayer`) instead of the custom HTML5 `<video>` player.
 */

/** Accepts plain seconds ("576") or the "1h2m3s" YouTube timestamp form. */
export function parseYouTubeTime(raw: string): number | undefined {
  if (/^\d+$/.test(raw)) return Number(raw)
  const m = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/)
  if (!m) return undefined
  const total = (Number(m[1]) || 0) * 3600 + (Number(m[2]) || 0) * 60 + (Number(m[3]) || 0)
  return total || undefined
}

/** Parse a YouTube watch/short/embed URL into its video id + optional start offset. */
export function parseYouTube(href: string): { id: string; start?: number } | null {
  try {
    const url = new URL(href)
    const host = url.hostname.replace(/^www\./, '').replace(/^m\./, '')
    let id: string | null = null
    if (host === 'youtu.be') {
      id = url.pathname.slice(1) || null
    } else if (host === 'youtube.com') {
      if (url.pathname === '/watch') id = url.searchParams.get('v')
      else if (url.pathname.startsWith('/embed/')) id = url.pathname.slice('/embed/'.length)
    }
    if (!id) return null
    const t = url.searchParams.get('t') ?? url.searchParams.get('start')
    return { id, start: t ? parseYouTubeTime(t) : undefined }
  } catch {
    return null
  }
}
